import { BulkDeleteDialog } from "@/components/transactions/bulk-delete-dialog";
import { DeleteTransactionDialog } from "@/components/transactions/delete-transaction-dialog";
import { FloatingBulkActions } from "@/components/transactions/floating-bulk-actions";
import { TransactionCreateDialog } from "@/components/transactions/transaction-create-dialog";
import { TransactionEditDialog } from "@/components/transactions/transaction-edit-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpc } from "@/utils/trpc";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Calendar,
  CreditCard,
  Edit,
  Eye,
  MoreHorizontal,
  Plus,
  Repeat,
  Search,
  SortAsc,
  SortDesc,
  Trash2,
} from "lucide-react";
import { useState, useEffect } from "react";

interface TransactionListProps {
  selectedPeriod?: {
    startDate: Date;
    endDate: Date;
    type: "current" | "month" | "custom";
  };
}

function formatCurrency(amount: number) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(amount);
}

function formatDate(date: Date) {
  return new Intl.DateTimeFormat("pt-BR", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(date));
}

export function TransactionList({ selectedPeriod }: TransactionListProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(
    undefined
  );
  const [typeFilter, setTypeFilter] = useState<"credit" | "debit" | undefined>(
    undefined
  );
  const [tagFilters, setTagFilters] = useState<string[]>([]);
  const [matchAll, setMatchAll] = useState(false);
  const [limit, setLimit] = useState(20);
  const [includeVirtual, setIncludeVirtual] = useState(true);
  const [showFutureOnly, setShowFutureOnly] = useState(false);
  const [sortBy, setSortBy] = useState<'date' | 'amount'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [editingTransactionId, setEditingTransactionId] = useState<
    string | null
  >(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [deletingTransactionId, setDeletingTransactionId] = useState<
    string | null
  >(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState<string[]>(
    []
  );
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);

  // Debounce search term to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch categories for filter dropdown
  const categories = useQuery(trpc.categories.list.queryOptions({}));
  const tagList = useQuery(trpc.tags.list.queryOptions({}));

  // Use hybrid timeline if virtual transactions are enabled and period is selected
  const useHybridTimeline = includeVirtual && selectedPeriod;

  const transactions = useQuery(
    useHybridTimeline
      ? trpc.transactions.hybridTimeline.queryOptions({
          startDate: selectedPeriod.startDate.toISOString(),
          endDate: selectedPeriod.endDate.toISOString(),
          includeVirtual,
          limit,
          categoryId: categoryFilter,
          type: typeFilter,
          tags: tagFilters.length ? tagFilters : undefined,
          matchAll,
        })
      : trpc.transactions.list.queryOptions({
          limit,
          searchTerm: debouncedSearchTerm || undefined,
          categoryId: categoryFilter,
          type: typeFilter,
          sortBy,
          sortOrder,
          startDate: selectedPeriod?.startDate.toISOString(),
          endDate: selectedPeriod?.endDate.toISOString(),
          tags: tagFilters.length ? tagFilters : undefined,
          matchAll,
        })
  );

  const { data, isPending } = transactions;

  const queryClient = useQueryClient();

  // Delete transaction mutation
  const deleteTransactionMutation = useMutation(
    trpc.transactions.delete.mutationOptions()
  );

  // Delete multiple transactions mutation
  const deleteBulkMutation = useMutation(
    trpc.transactions.deleteBulk.mutationOptions()
  );

  // Handle edit transaction
  const handleEditTransaction = (transactionId: string) => {
    setEditingTransactionId(transactionId);
    setShowEditDialog(true);
  };

  // Handle delete transaction
  const handleDeleteTransaction = (transactionId: string) => {
    setDeletingTransactionId(transactionId);
    setShowDeleteDialog(true);
  };

  // Confirm delete transaction
  const confirmDeleteTransaction = async () => {
    if (!deletingTransactionId) return;

    try {
      await deleteTransactionMutation.mutateAsync({
        id: deletingTransactionId,
      });
      // Invalidate and refetch transactions
      queryClient.invalidateQueries();
      setShowDeleteDialog(false);
      setDeletingTransactionId(null);
    } catch (error) {
      console.error("Erro ao deletar transação:", error);
      alert("Erro ao deletar transação. Tente novamente.");
    }
  };

  // Handle edit dialog close
  const handleEditDialogClose = () => {
    setShowEditDialog(false);
    setEditingTransactionId(null);
  };

  // Handle bulk operations
  const handleSelectTransaction = (transactionId: string, checked: boolean) => {
    if (checked) {
      setSelectedTransactions((prev) => [...prev, transactionId]);
    } else {
      setSelectedTransactions((prev) =>
        prev.filter((id) => id !== transactionId)
      );
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Select all non-virtual transactions
      const selectableTransactions = transactions_data
        .filter((t) => !t.isVirtual)
        .map((t) => t.id);
      setSelectedTransactions(selectableTransactions);
    } else {
      setSelectedTransactions([]);
    }
  };

  const handleBulkDelete = () => {
    if (selectedTransactions.length > 0) {
      setShowBulkDeleteDialog(true);
    }
  };

  const confirmBulkDelete = async () => {
    if (selectedTransactions.length === 0) return;

    try {
      await deleteBulkMutation.mutateAsync({ ids: selectedTransactions });
      // Invalidate and refetch transactions
      queryClient.invalidateQueries();
      setShowBulkDeleteDialog(false);
      setSelectedTransactions([]);
    } catch (error) {
      console.error("Erro ao deletar transações:", error);
      alert("Erro ao deletar transações. Tente novamente.");
    }
  };

  const clearSelection = () => {
    setSelectedTransactions([]);
  };

  if (isPending) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Transações Recentes</CardTitle>
          <CardDescription>
            Carregando sua atividade financeira recente...
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <div className="flex items-center space-x-4 py-2" key={i}>
                <div className="h-8 w-8 animate-pulse rounded-full bg-muted" />
                <div className="flex-1 space-y-1">
                  <div className="h-4 animate-pulse rounded bg-muted" />
                  <div className="h-3 w-2/3 animate-pulse rounded bg-muted" />
                </div>
                <div className="h-4 w-20 animate-pulse rounded bg-muted" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  let transactions_data = data?.transactions || [];

  // Apply future transactions filter
  if (showFutureOnly) {
    transactions_data = transactions_data.filter(
      (t) => !t.isVirtual && new Date(t.date) > new Date()
    );
  }

  const total = data?.total || 0;
  const realCount = "realCount" in (data || {}) ? data.realCount : total;
  const virtualCount = "virtualCount" in (data || {}) ? data.virtualCount : 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Transações</CardTitle>
            <CardDescription>
              {useHybridTimeline ? (
                <>
                  {realCount} reais • {virtualCount} projetadas • Total: {total}
                </>
              ) : (
                <>
                  {total} transações • Mostrando {transactions_data.length}
                </>
              )}
            </CardDescription>
          </div>
          <Button
            className="gap-2"
            onClick={() => setShowCreateDialog(true)}
            size="sm"
          >
            <Plus className="h-4 w-4" />
            Adicionar Transação
          </Button>
        </div>

        {/* Search and Filters */}
        <div className="flex items-center space-x-2">
          {/* Virtual transactions toggle */}
          {selectedPeriod && (
            <div className="mr-4 flex items-center space-x-2">
              <Switch
                checked={includeVirtual}
                id="include-virtual"
                onCheckedChange={setIncludeVirtual}
              />
              <label
                className="flex cursor-pointer items-center gap-1 text-muted-foreground text-sm"
                htmlFor="include-virtual"
              >
                <Eye className="h-3 w-3" />
                Projeções
              </label>
            </div>
          )}

          {/* Future transactions toggle */}
          <div className="mr-4 flex items-center space-x-2">
            <Switch
              checked={showFutureOnly}
              id="show-future"
              onCheckedChange={setShowFutureOnly}
            />
            <label
              className="flex cursor-pointer items-center gap-1 text-muted-foreground text-sm"
              htmlFor="show-future"
            >
              <Calendar className="h-3 w-3" />
              Apenas Futuras
            </label>
          </div>

          {!useHybridTimeline && (
            <div className="relative flex-1">
              <Search className="absolute top-3 left-3 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar transações..."
                value={searchTerm}
              />
            </div>
          )}
          <Select
            onValueChange={(value: string) =>
              setTypeFilter(value === "all" ? undefined : value as "credit" | "debit")
            }
            value={typeFilter || "all"}
          >
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="credit">Receitas</SelectItem>
              <SelectItem value="debit">Gastos</SelectItem>
            </SelectContent>
          </Select>
          <Select
            onValueChange={(value) =>
              setCategoryFilter(value === "all" ? undefined : value)
            }
            value={categoryFilter || "all"}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Categoria" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {categories.data?.map((category) => (
                <SelectItem key={category.id} value={category.id}>
                  {category.icon} {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Sort Controls */}
          <Select
            onValueChange={(value: string) => setSortBy(value as 'date' | 'amount')}
            value={sortBy}
          >
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Ordenar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="date">Data</SelectItem>
              <SelectItem value="amount">Valor</SelectItem>
            </SelectContent>
          </Select>

          <Button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            size="sm"
            variant="outline"
            className="flex items-center gap-1"
          >
            {sortOrder === 'asc' ? (
              <SortAsc className="h-4 w-4" />
            ) : (
              <SortDesc className="h-4 w-4" />
            )}
            {sortOrder === 'asc' ? 'Crescente' : 'Decrescente'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Tag filters */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {(tagList.data || []).slice(0, 12).map((t: { tag: string; name: string; count: number; spendingLimit: number | null }) => {
            const active = tagFilters.includes(t.tag);
            return (
              <Button
                key={t.tag}
                onClick={() =>
                  setTagFilters((prev) =>
                    active ? prev.filter((x) => x !== t.tag) : [...prev, t.tag]
                  )
                }
                size="sm"
                variant={active ? "default" : "outline"}
              >
                #{t.tag}
              </Button>
            );
          })}
          <label className="ml-2 flex items-center gap-2 text-muted-foreground text-sm">
            <input
              checked={matchAll}
              onChange={(e) => setMatchAll(e.target.checked)}
              type="checkbox"
            />
            Match ALL
          </label>
          {tagFilters.length > 0 && (
            <Button onClick={() => setTagFilters([])} size="sm" variant="ghost">
              Limpar tags
            </Button>
          )}
        </div>
        {transactions_data.length === 0 ? (
          <div className="py-6 text-center">
            <div className="mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-full bg-muted">
              <ArrowUpCircle className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="mb-1 font-semibold text-lg">
              Nenhuma transação ainda
            </h3>
            <p className="mb-4 text-muted-foreground">
              {searchTerm
                ? `Nenhuma transação encontrada para "${searchTerm}"`
                : "Comece a acompanhar suas finanças adicionando sua primeira transação"}
            </p>
            <Button onClick={() => setShowCreateDialog(true)}>
              Adicionar Transação
            </Button>
          </div>
        ) : (
          <>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">
                      <Checkbox
                        aria-label="Selecionar todas as transações"
                        checked={
                          selectedTransactions.length > 0 &&
                          selectedTransactions.length ===
                            transactions_data.filter((t) => !t.isVirtual).length
                        }
                        onCheckedChange={handleSelectAll}
                      />
                    </TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                    <TableHead className="w-[50px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions_data.map((transaction) => (
                    <TableRow
                      className={
                        transaction.isVirtual
                          ? "bg-muted/30 opacity-70"
                          : !transaction.isVirtual && new Date(transaction.date) > new Date()
                          ? "bg-blue-50/40 dark:bg-blue-950/20 border-l-2 border-l-blue-500"
                          : ""
                      }
                      key={transaction.id}
                    >
                      <TableCell>
                        {!transaction.isVirtual && (
                          <Checkbox
                            aria-label={`Selecionar transação ${transaction.description}`}
                            checked={selectedTransactions.includes(
                              transaction.id
                            )}
                            onCheckedChange={(checked) =>
                              handleSelectTransaction(
                                transaction.id,
                                checked as boolean
                              )
                            }
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          {transaction.type === "credit" ? (
                            <ArrowUpCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <ArrowDownCircle className="h-4 w-4 text-red-600" />
                          )}
                          <div className="flex items-center gap-1">
                            <Badge
                              className={
                                transaction.type === "credit"
                                  ? "bg-green-500/20 text-green-700 dark:text-green-300"
                                  : "bg-red-500/20 text-red-700 dark:text-red-300"
                              }
                              variant={
                                transaction.type === "credit"
                                  ? "default"
                                  : "secondary"
                              }
                            >
                              {transaction.type === "credit"
                                ? "Receita"
                                : "Gasto"}
                            </Badge>
                            {transaction.isVirtual && (
                              <Badge className="text-xs" variant="outline">
                                <Repeat className="mr-1 h-3 w-3" />
                                Projeção
                              </Badge>
                            )}
                            {/* Future transaction badge */}
                            {!transaction.isVirtual && new Date(transaction.date) > new Date() && (
                              <Badge className="bg-blue-500/20 text-blue-700 text-xs dark:text-blue-300" variant="outline">
                                <Calendar className="mr-1 h-3 w-3" />
                                Futuro
                              </Badge>
                            )}
                            {/* Installment badge - shows for transactions with multiple installments */}
                            {transaction.totalInstallments &&
                              transaction.totalInstallments > 1 && (
                                <Badge
                                  className="bg-blue-500/20 text-blue-700 text-xs dark:text-blue-300"
                                  variant="outline"
                                >
                                  <CreditCard className="mr-1 h-3 w-3" />
                                  {transaction.installmentNumber}/
                                  {transaction.totalInstallments}
                                </Badge>
                              )}
                            {/* Temporary debug info */}
                            {process.env.NODE_ENV === 'development' && transaction.description?.includes('HBL') && (
                              <span className="text-xs text-orange-500 ml-2">
                                [DEBUG: totalInstallments={transaction.totalInstallments}, installmentNumber={transaction.installmentNumber}]
                              </span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">
                          {transaction.description || "Sem descrição"}
                        </div>
                        {transaction.tags && transaction.tags.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {transaction.tags.map((tag) => (
                              <Badge
                                className="text-xs"
                                key={tag}
                                variant="outline"
                              >
                                #{tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {transaction.category ? (
                          <Badge variant="outline">
                            {transaction.category.name}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">
                            Sem categoria
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(new Date(transaction.date))}
                      </TableCell>
                      <TableCell
                        className={`text-right font-medium font-mono ${
                          transaction.type === "credit"
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {transaction.type === "credit" ? "+" : "-"}
                        {formatCurrency(Number(transaction.amount))}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              className="h-8 w-8 opacity-100 hover:bg-accent hover:opacity-100"
                              disabled={transaction.isVirtual}
                              size="icon"
                              variant="ghost"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              className="flex cursor-pointer items-center gap-2"
                              onClick={() =>
                                handleEditTransaction(transaction.id)
                              }
                            >
                              <Edit className="h-4 w-4" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="flex cursor-pointer items-center gap-2 text-red-600 hover:text-red-700"
                              onClick={() =>
                                handleDeleteTransaction(transaction.id)
                              }
                            >
                              <Trash2 className="h-4 w-4" />
                              Deletar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Load More */}
            {total > transactions_data.length && (
              <div className="mt-4 text-center">
                <Button
                  onClick={() => setLimit((prev) => prev + 20)}
                  variant="outline"
                >
                  Carregar Mais ({total - transactions_data.length} restantes)
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>

      {/* Transaction Edit Dialog */}
      <TransactionEditDialog
        onOpenChange={setShowEditDialog}
        onSuccess={handleEditDialogClose}
        open={showEditDialog}
        transactionId={editingTransactionId}
      />

      {/* Transaction Create Dialog */}
      <TransactionCreateDialog
        onOpenChange={setShowCreateDialog}
        onSuccess={() => {
          setShowCreateDialog(false);
          // Refresh the transaction list
          transactions.refetch();
        }}
        open={showCreateDialog}
      />

      {/* Delete Transaction Dialog */}
      <DeleteTransactionDialog
        isLoading={deleteTransactionMutation.isPending}
        onConfirm={confirmDeleteTransaction}
        onOpenChange={setShowDeleteDialog}
        open={showDeleteDialog}
        transactionDescription={
          deletingTransactionId
            ? transactions_data.find((t: any) => t.id === deletingTransactionId)?.description
            : undefined
        }
      />

      {/* Bulk Delete Dialog */}
      <BulkDeleteDialog
        isLoading={deleteBulkMutation.isPending}
        onConfirm={confirmBulkDelete}
        onOpenChange={setShowBulkDeleteDialog}
        open={showBulkDeleteDialog}
        selectedCount={selectedTransactions.length}
      />

      {/* Floating Bulk Actions */}
      <FloatingBulkActions
        isLoading={deleteBulkMutation.isPending}
        onBulkDelete={handleBulkDelete}
        onClearSelection={clearSelection}
        selectedCount={selectedTransactions.length}
      />
    </Card>
  );
}
